import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { DollarSign, Loader2 } from 'lucide-react';
import adminAxios from './adminAxios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';

const currency = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const STATUSES = [
  { id: 'all', label: 'Todos' },
  { id: 'pending', label: 'Pendente' },
  { id: 'approved', label: 'Aprovado' },
  { id: 'to_pay', label: 'A Pagar' },
  { id: 'paid', label: 'Pago' },
  { id: 'cancelled', label: 'Cancelado' },
];

const NEXT_STATUS = {
  pending: 'approved',
  approved: 'to_pay',
  to_pay: 'paid',
};

const AdminPayouts = () => {
  const [filter, setFilter] = useState('pending');
  const [data, setData] = useState({ requests: [], summary: {}, token_value: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?payout_status=${filter}` : '';
      const { data } = await adminAxios.get(`/admin/payouts${params}`);
      setData(data);
    } catch (err) {
      toast.error('Erro ao carregar');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (r, newStatus) => {
    try {
      await adminAxios.post(`/admin/payouts/${r.request_id}/status`, { payout_status: newStatus });
      toast.success(`Atualizado para ${newStatus}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro');
    }
  };

  return (
    <div className="p-4" data-testid="admin-payouts">
      <h2 className="text-2xl font-black text-slate-900 mb-4 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
        <DollarSign className="w-6 h-6 text-emerald-600" /> Repasses aos Profissionais
      </h2>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        {Object.entries(data.summary || {}).map(([st, val]) => (
          <Card key={st} className="p-3" data-testid={`summary-${st}`}>
            <p className="text-[10px] uppercase text-slate-500 font-semibold">{st}</p>
            <p className="text-lg font-black text-slate-900">{currency(val)}</p>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {STATUSES.map((s) => (
          <button
            key={s.id}
            onClick={() => setFilter(s.id)}
            data-testid={`payouts-filter-${s.id}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
              filter === s.id ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-600" /></div>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left border-b border-slate-200">
                <th className="py-2 px-3">Data</th>
                <th>Profissional</th>
                <th>Cliente</th>
                <th>Plano</th>
                <th>Status</th>
                <th className="text-right">Valor</th>
                <th className="text-right pr-3">Ação</th>
              </tr>
            </thead>
            <tbody data-testid="payouts-table-body">
              {data.requests.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-6 text-slate-500">Nenhum repasse nesta categoria</td></tr>
              ) : data.requests.map((r) => (
                <tr key={r.request_id} className="border-b border-slate-100">
                  <td className="py-2 px-3 text-xs">{new Date(r.requested_at).toLocaleDateString('pt-BR')}</td>
                  <td className="text-sm font-semibold">{r.professional_name}</td>
                  <td className="text-xs">{r.student_name}</td>
                  <td className="text-xs">{r.user_plan_id}</td>
                  <td>
                    <span className="text-[10px] uppercase font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                      {r.payout_status}
                    </span>
                  </td>
                  <td className="text-right font-bold text-teal-700">{currency(r.payout_amount)}</td>
                  <td className="text-right pr-3">
                    {NEXT_STATUS[r.payout_status] && (
                      <Button
                        size="sm"
                        onClick={() => updateStatus(r, NEXT_STATUS[r.payout_status])}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] h-7"
                        data-testid={`advance-${r.request_id}`}
                      >
                        → {NEXT_STATUS[r.payout_status]}
                      </Button>
                    )}
                    {r.payout_status !== 'cancelled' && r.payout_status !== 'paid' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => updateStatus(r, 'cancelled')}
                        className="text-[10px] h-7 ml-1"
                        data-testid={`cancel-${r.request_id}`}
                      >
                        Cancelar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default AdminPayouts;
